import useCreateDeposit from '@/lib/api/create-deposit';
import { defaultChain, wagmiConfig } from '@/lib/wagmi/config';
import { useAccountStore } from '@/store/use-account-store';
import { getUSDCAddress } from '@/utils/addresses';
import { NativeFundingConfig, useFundWallet } from '@privy-io/react-auth';
import { getBalance } from '@wagmi/core';

let balanceIterationsLeft = 0;

type Props = {
  retries?: number;
  amountNeeded?: bigint;
  onBalanceChange?: (newBalance: bigint) => void;
};

export default function usePrivyFunding({
  retries = 50,
  amountNeeded,
  onBalanceChange,
}: Props = {}) {
  const { createDeposit } = useCreateDeposit();
  const address = useAccountStore((state) => state.address) as `0x${string}`;

  const { fundWallet } = useFundWallet({
    onUserExited: async () => {
      console.log('Funding exited.');
      if (!address) return;

      if (balanceIterationsLeft > 0) {
        balanceIterationsLeft = retries;
        return;
      }

      //   const toastId = toast.loading('Waiting for your funds...');

      const checkBalance = async () => {
        const newBalance = await getBalance(wagmiConfig, {
          address,
          blockTag: 'latest',
          token: getUSDCAddress(defaultChain.id),
        });

        if (
          newBalance.value > 0n &&
          (!amountNeeded || newBalance.value >= amountNeeded)
        ) {
          balanceIterationsLeft = 0;
        } else {
          balanceIterationsLeft -= 1;
        }

        if (!amountNeeded) {
          onBalanceChange?.(0n);
          if (newBalance.value > 0n) {
            await createDeposit({
              size: newBalance.value,
              chainId: defaultChain.id,
            });
          }
        } else if (newBalance.value >= amountNeeded) {
          onBalanceChange?.(newBalance.value);
          await createDeposit({
            size: newBalance.value,
            chainId: defaultChain.id,
          });
        }
      };

      balanceIterationsLeft = retries - balanceIterationsLeft;

      while (balanceIterationsLeft > 0) {
        await checkBalance();
        await new Promise((res) => window.setTimeout(res, 2000));
      }

      //   toast.dismiss(toastId);
    },
  });

  return {
    fundWallet: (
      options: {
        amount?: string;
        defaultFundingMethod?: NativeFundingConfig['defaultFundingMethod'];
      } = {},
    ) => {
      if (!address) return;
      return fundWallet(address, {
        chain: defaultChain,
        asset: 'USDC',
        uiConfig: {
          receiveFundsTitle: 'Receive funds',
        },
        ...options,
      });
    },
    isLoading: balanceIterationsLeft > 0,
  };
}
